"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const constants_1 = require("./constants");
const utils_1 = require("./utils");
const querystring = require("querystring");
const Error_1 = require("./Error");
class Client {
    constructor(options) {
        this._currentFrame = null;
        this._customerToken = null;
        this._onExit = () => { };
        this._onError = () => { };
        this._onEvent = () => { };
        this._onReady = () => { };
        const { environment, publishableKey, integrationId, host, apiVersion } = options;
        if (environment !== constants_1.ENVIRONMENT.sandbox &&
            environment !== constants_1.ENVIRONMENT.live) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.INVALID_FIELD, constants_1.ERROR_MESSAGE.BAD_ENVIRONMENT, 'environment');
        }
        if (!publishableKey) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'publishableKey');
        }
        // TODO: Uncomment missing integrationId handler below when we are ready to enforce it
        // if (!integrationId) {
        //     throw new StrongholdPayError(
        //         ERROR_TYPE.VALIDATION_ERROR,
        //         ERROR_CODE.MISSING_FIELD,
        //         ERROR_MESSAGE.ATTRIBUTE_REQUIRED,
        //         'integrationId',
        //     )
        // }
        if (apiVersion != null && apiVersion.toString().length > 0) {
            if (!apiVersion.toString().startsWith('v')) {
                // throw error
                throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.INVALID_FIELD, constants_1.ERROR_MESSAGE.BAD_API_VERSION, 'apiVersion');
            }
            this._apiVersion = apiVersion;
        }
        else {
            this._apiVersion = "v2"; // Default version is v2
        }
        this._currentFrame = null;
        this._publishableKey = publishableKey;
        this._environment = environment;
        this._integrationId = integrationId;
        if (host) {
            this._host = host;
        }
        else {
            this._host = constants_1.HOST;
        }
    }
    setOptions(options = {}) {
        this._onExit = options.onExit || this._onExit;
        this._onError = options.onError || this._onError;
        this._onEvent = options.onEvent || this._onEvent;
        this._onReady = options.onReady || this._onReady;
    }
    cleanFrame() {
        var _a;
        (_a = this._currentFrame) === null || _a === void 0 ? void 0 : _a.remove();
        this._currentFrame = null;
    }
    isValidEvent(event) {
        return event.origin === this._host || !!event.data.payload;
    }
    exit(event) {
        this.cleanFrame();
        this._onExit();
    }
    error(event) {
        this._onError(event.data.payload.err ||
            new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.API_ERROR, constants_1.ERROR_CODE.SERVER_ERROR, 'something went wrong'));
    }
    ready(event) {
        this._onReady();
    }
    buildFrame(route, data = {}, checkCustomerToken = true) {
        if (checkCustomerToken && !this._customerToken) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'customer_token');
        }
        const query = querystring.stringify(Object.assign({ publishableKey: this._publishableKey, customerToken: this._customerToken, integrationId: this._integrationId, apiVersion: this._apiVersion }, data));
        const src = `${this._host}/dropin${route}?${query}`;
        this._currentFrame = utils_1.frameForSrc(src);
    }
    addEventListener(onSuccess) {
        const handler = (e) => {
            const event = e;
            if (!this.isValidEvent(event) || !this._currentFrame) {
                return;
            }
            this._onEvent(event);
            if (event.data.event === constants_1.EVENT.READY) {
                this.ready(event);
            }
            if (event.data.event === constants_1.EVENT.EXIT) {
                this.exit(event);
                window.removeEventListener('message', handler);
            }
            if (event.data.event === constants_1.EVENT.ERROR) {
                this.error(event);
            }
            if (event.data.event === constants_1.EVENT.SUCCESS && onSuccess) {
                onSuccess(event.data.payload.data);
            }
        };
        window.removeEventListener('message', handler, false);
        window.addEventListener('message', handler, false);
    }
    setCustomerToken(customerToken) {
        this._customerToken = customerToken;
    }
    addPaymentSource(customerToken, options) {
        this.cleanFrame();
        this.setOptions(options);
        this.setCustomerToken(customerToken);
        this.addEventListener(options.onSuccess);
        this.buildFrame('/paymentsources/add', Object.assign({}, utils_1.getOptionQuery(options)));
    }
    updatePaymentSource(customerToken, options) {
        if (!options.paymentSourceId) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'payment_source_id');
        }
        this.cleanFrame();
        this.setOptions(options);
        this.setCustomerToken(customerToken);
        this.addEventListener(options.onSuccess);
        this.buildFrame('/paymentsources/update', Object.assign({ paymentSourceId: options.paymentSourceId }, utils_1.getOptionQuery(options)));
    }
    charge(customerToken, options) {
        if (!options.charge) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'charge');
        }
        if (!options.charge.paymentSourceId) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'charge.payment_source_id');
        }
        if (!options.charge.amount) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'charge.amount');
        }
        this.cleanFrame();
        this.setOptions(options);
        this.setCustomerToken(customerToken);
        this.addEventListener(options.onSuccess);
        this.buildFrame('/charges/add', Object.assign(Object.assign(Object.assign({ authorizeOnly: options.authorizeOnly, skipPayScreen: options.skipPayScreen, skipSuccessScreen: options.skipSuccessScreen }, utils_1.getOptionQuery(options)), utils_1.getChargeQuery(options.charge)), utils_1.getTipQuery(options.tip)));
    }
    tip(customerToken, options) {
        if (!options.tip) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'tip');
        }
        if (!options.tip.chargeId) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'tip.charge_id');
        }
        if (!options.tip.paymentSourceId) {
            throw new Error_1.StrongholdPayError(constants_1.ERROR_TYPE.VALIDATION_ERROR, constants_1.ERROR_CODE.MISSING_FIELD, constants_1.ERROR_MESSAGE.ATTRIBUTE_REQUIRED, 'tip.payment_source_id');
        }
        this.cleanFrame();
        this.setOptions(options);
        this.setCustomerToken(customerToken);
        this.addEventListener(options.onSuccess);
        this.buildFrame('/tips/add', Object.assign(Object.assign({ authorizeOnly: options.authorizeOnly, skipPayScreen: options.skipPayScreen, skipSuccessScreen: options.skipSuccessScreen }, utils_1.getOptionQuery(options)), utils_1.getTipQuery(options.tip)));
    }
    findCustomer(options) {
        this.cleanFrame();
        this.setOptions(options);
        this.addEventListener(options.onSuccess);
        this.buildFrame('/customers', Object.assign({ isLogin: options.isLogin }, utils_1.getOptionQuery(options)), false);
    }
}
exports.Client = Client;
//# sourceMappingURL=Client.js.map